export default function filterBiddingSuccessData(data = [], filters = {}) {
  const {
    biddingTypeSelecionado = '',
    period = 'monthly', // 'daily', 'weekly', 'monthly', 'quarterly'
    year = new Date().getFullYear(),
    month = '', // filtro mês usado quando period === 'weekly' ou 'daily'
  } = filters

  // Valida se a data é do ano selecionado
  function isDateInYear(dateStr) {
    if (!dateStr) return false
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return false
    return date.getFullYear() === Number(year)
  }

  // Valida se a data está no mês selecionado (quando aplicável)
  function isDateInMonth(dateStr) {
    if (!dateStr) return false
    if (!month) return true // se mês não selecionado, aceita qualquer mês
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return false
    return (date.getMonth() + 1) === Number(month)
  }

  // Retorna segunda-feira da semana da data (para chave semanal)
  function getStartOfWeek(date) {
    const day = date.getDay() // domingo=0, segunda=1, ...
    const diff = (day === 0 ? -6 : 1) - day // diferença para segunda
    const monday = new Date(date)
    monday.setDate(date.getDate() + diff)
    monday.setHours(0, 0, 0, 0)
    return monday
  }

  // Formata data para dd/MM/yyyy
  function formatDateBR(dateStr) {
    const d = new Date(dateStr)
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
  }

  // Formata label mês/ano para MMM/yyyy (abreviação simples)
  function formatMonthYear(dateKey) {
    const [yearStr, monthStr] = dateKey.split('-')
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    return `${months[Number(monthStr) - 1]}/${yearStr}`
  }

  // Formata label trimestre ex: "Q2/2023"
  function formatQuarterLabel(qKey) {
    const [yearStr, q] = qKey.split('-Q')
    return `Q${q}/${yearStr}`
  }

  // Formata label semana: "dd/MM - dd/MM"
  function formatWeekLabel(startDateStr) {
    const start = new Date(startDateStr)
    const end = new Date(start)
    end.setDate(start.getDate() + 6)
    const pad = n => String(n).padStart(2, '0')
    return `${pad(start.getDate())}/${pad(start.getMonth() + 1)} - ${pad(end.getDate())}/${pad(end.getMonth() + 1)}`
  }

  // Filtra dados conforme tipo licitação, ano e mês
  const dadosFiltrados = data.filter(item => {
    if (biddingTypeSelecionado && item.biddingType !== biddingTypeSelecionado) return false
    if (!isDateInYear(item.date)) return false

    // se for semanal ou diário e mês selecionado, filtra pelo mês
    if (
      (period === 'weekly' || period === 'daily')
      && month
      && !isDateInMonth(item.date)
    ) return false

    return true
  })

  // Agrupa dados por período
  const grouped = {}

  dadosFiltrados.forEach(item => {
    let periodKey = ''
    const date = new Date(item.date)
    if (Number.isNaN(date.getTime())) return

    switch (period) {
      case 'daily':
        periodKey = (() => {
          const [dateOnly] = item.date.split('T')
          return dateOnly
        })()
        break
      case 'weekly': {
        const monday = getStartOfWeek(date)
        periodKey = monday.toISOString().slice(0, 10)
        break
      }
      case 'monthly': {
        const yearNum = date.getFullYear()
        const monthNum = String(date.getMonth() + 1).padStart(2, '0')
        periodKey = `${yearNum}-${monthNum}`
        break
      }
      case 'quarterly': {
        const quarter = Math.floor(date.getMonth() / 3) + 1
        const yearNum = date.getFullYear()
        periodKey = `${yearNum}-Q${quarter}`
        break
      }
      default: {
        const yearNum = date.getFullYear()
        const monthNum = String(date.getMonth() + 1).padStart(2, '0')
        periodKey = `${yearNum}-${monthNum}`
      }
    }

    if (!grouped[periodKey]) {
      grouped[periodKey] = {
        oportunidades: new Set(),
        ganhosPregoes: new Set(),
        itensGanhos: 0,
      }
    }

    const faseNormalizada = (item.phase || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

    if (
      faseNormalizada !== 'SEM INTERESSE'
      && faseNormalizada !== 'NAO PARTICIPOU'
      && item.nPregao
    ) {
      grouped[periodKey].oportunidades.add(item.nPregao)
    }

    if (
      faseNormalizada === 'GANHOS'
      && item.nPregao
    ) {
      grouped[periodKey].ganhosPregoes.add(item.nPregao)
      grouped[periodKey].itensGanhos += 1
    }
  })

  // Ordena períodos para exibição
  const periodsSorted = Object.keys(grouped).sort()

  // Monta labels legíveis conforme período
  const labels = periodsSorted.map(p => {
    switch (period) {
      case 'daily':
        return formatDateBR(p)
      case 'weekly':
        return formatWeekLabel(p)
      case 'monthly':
        return formatMonthYear(p)
      case 'quarterly':
        return formatQuarterLabel(p)
      default:
        return p
    }
  })

  // Prepara dados para gráfico
  const totalBiddingsWon = periodsSorted.map(p => grouped[p].ganhosPregoes.size)
  const totalItemsWon = periodsSorted.map(p => grouped[p].itensGanhos)
  const conversionRates = periodsSorted.map(p => {
    const opp = grouped[p].oportunidades.size
    const wins = grouped[p].ganhosPregoes.size
    return opp > 0 ? (wins / opp) * 100 : 0
  })

  // Retorna dados para usar no gráfico
  return {
    labels,
    datasets: [
      {
        label: 'Licitações Ganhas',
        backgroundColor: '#4CAF50',
        data: totalBiddingsWon,
      },
      {
        label: 'Itens Ganhos',
        backgroundColor: '#2196F3',
        data: totalItemsWon,
      },
      {
        label: 'Taxa de Conversão (%)',
        backgroundColor: '#FFC107',
        data: conversionRates.map(v => Number(v.toFixed(2))),
      },
    ],
  }
}
